import { useCallback, useEffect, useRef, useState } from 'react';

import { AprovacaoDeParticipantes, RealizacaoParaAutorizar, statusDaAutorizacao, tamanhoDaEquipe } from './autorizacao-regras';
import { desfazerDecisao, listarRealizacoes, registrarDecisao } from './autorizacao.service';

/** `sem-ciclo`: o calendário do ciclo vigente não tem a Apreciação Nível 3, que dá o fim da janela (PAR-85). */
export type EstadoDaCarga = 'carregando' | 'erro' | 'pronto' | 'sem-ciclo';

interface Periodo {
  inicio: number;
  fim: number;
}

interface Secoes {
  /** Pendentes na carga: ficam nesta seção mesmo depois de decididas na sessão. */
  aguardando: string[];
  avaliadas: string[];
}

export const ANUNCIO_DESFEITO = 'Decisão desfeita. A realização voltou a aguardar sua autorização.';

const anuncioDaDecisao = (aprovada: boolean) => (aprovada ? 'Quantidade autorizada por você.' : 'Quantidade não autorizada por você.');

/** Estado local depois de a decisão gravar: o mesmo que o trigger grava (PAR-30). */
function decidida(realizacao: RealizacaoParaAutorizar, aprovada: boolean, usuario: string): RealizacaoParaAutorizar {
  const decisao = aprovada ? 'approved' : 'rejected';
  return {
    ...realizacao,
    participants_approval: {
      ...realizacao.participants_approval,
      status: decisao,
      decision: decisao,
      decided_by: usuario,
      decided_team_size: tamanhoDaEquipe(realizacao),
    },
  };
}

/** Estado local depois de o desfazer gravar (PAR-31). */
function desfeita(realizacao: RealizacaoParaAutorizar): RealizacaoParaAutorizar {
  const aprovacao: AprovacaoDeParticipantes = {
    ...realizacao.participants_approval,
    status: 'pending',
    decision: undefined,
    decided_by: undefined,
    decided_team_size: undefined,
  };
  return { ...realizacao, participants_approval: aprovacao };
}

/**
 * Realizações da página de autorização: uma leitura por gestor e período, gravação por cartão e
 * transição local entre pendente e resolvido sem reler a lista (PAR-12, PAR-13, PAR-22 a PAR-25, PAR-38, PAR-39).
 * `periodo` é `undefined` enquanto o calendário carrega e `null` quando ele não tem a Apreciação Nível 3 (PAR-85).
 */
export function useAutorizacoes(gestor: string | undefined, usuario: string | undefined, periodo: Periodo | null | undefined) {
  const [estado, setEstado] = useState<EstadoDaCarga>('carregando');
  const [realizacoes, setRealizacoes] = useState<Record<string, RealizacaoParaAutorizar>>({});
  const [secoes, setSecoes] = useState<Secoes>({ aguardando: [], avaliadas: [] });
  const [gravando, setGravando] = useState<Record<string, boolean>>({});
  const [erros, setErros] = useState<Record<string, string | null>>({});
  const [anuncio, setAnuncio] = useState('');
  const [tentativa, setTentativa] = useState(0);
  // Cartões com gravação em andamento, marcados antes do primeiro await: o estado `gravando` só chega no
  // próximo render, e dois cliques antes dele passariam juntos (PAR-87).
  const emAndamento = useRef(new Set<string>());

  const semCiclo = periodo === null;
  const inicio = periodo?.inicio;
  const fim = periodo?.fim;

  useEffect(() => {
    if (!gestor) return;
    if (semCiclo) {
      setEstado('sem-ciclo');
      return;
    }
    if (inicio === undefined || fim === undefined) return;
    let ativo = true;
    setEstado('carregando');

    listarRealizacoes(inicio, fim, gestor)
      .then(lista => {
        if (!ativo) return;
        setRealizacoes(Object.fromEntries(lista.map(item => [item._id, item])));
        setSecoes({
          aguardando: lista.filter(item => statusDaAutorizacao(item) === 'pending').map(item => item._id),
          avaliadas: lista.filter(item => ['approved', 'rejected'].includes(statusDaAutorizacao(item) ?? '')).map(item => item._id),
        });
        setErros({});
        setEstado('pronto');
      })
      .catch(() => ativo && setEstado('erro'));

    return () => {
      ativo = false;
    };
  }, [gestor, semCiclo, inicio, fim, tentativa]);

  const gravar = useCallback(
    async (id: string, gravacao: (realizacao: RealizacaoParaAutorizar) => Promise<void>, depois: (realizacao: RealizacaoParaAutorizar) => RealizacaoParaAutorizar, texto: string) => {
      const realizacao = realizacoes[id];
      if (!realizacao || emAndamento.current.has(id)) return;

      emAndamento.current.add(id);
      setGravando(atual => ({ ...atual, [id]: true }));
      setErros(atual => ({ ...atual, [id]: null }));
      try {
        await gravacao(realizacao);
        // A lista pode ter sido relida sem o cartão enquanto gravava (outro gestor, outro período): nada a atualizar (PAR-88).
        setRealizacoes(atual => (atual[id] ? { ...atual, [id]: depois(atual[id]) } : atual));
        setAnuncio(texto);
      } catch (erro) {
        setErros(atual => ({ ...atual, [id]: (erro as Error).message }));
      } finally {
        emAndamento.current.delete(id);
        setGravando(atual => ({ ...atual, [id]: false }));
      }
    },
    [realizacoes],
  );

  const decidir = useCallback(
    (id: string, aprovada: boolean) =>
      gravar(
        id,
        realizacao => registrarDecisao(realizacao, aprovada),
        realizacao => decidida(realizacao, aprovada, usuario ?? ''),
        anuncioDaDecisao(aprovada),
      ),
    [gravar, usuario],
  );

  const desfazer = useCallback((id: string) => gravar(id, desfazerDecisao, desfeita, ANUNCIO_DESFEITO), [gravar]);

  const lista = (ids: string[]) => ids.map(id => realizacoes[id]).filter(Boolean);
  const aguardando = lista(secoes.aguardando);
  const avaliadas = lista(secoes.avaliadas);
  const pendentes = [...aguardando, ...avaliadas].filter(item => statusDaAutorizacao(item) === 'pending').length;

  return {
    estado,
    aguardando,
    avaliadas,
    pendentes,
    gravando,
    erros,
    anuncio,
    decidir,
    desfazer,
    tentarDeNovo: () => setTentativa(n => n + 1),
  };
}
